import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { CoordinatesStore, Coordinates } from "./types";

const DEFAULT_COORDINATES: Coordinates = {
  lat: 50.7753,
  lng: 6.0839,
};

const useCoordinatesStore = create<CoordinatesStore>()(
  devtools(
    immer((set) => ({
      // State
      coordinates: DEFAULT_COORDINATES,

      // Actions
      updateCoordinates: (payload) =>
        set((state) => {
          const updates =
            typeof payload === "function"
              ? payload(state.coordinates)
              : payload;

          // Validate and convert to numbers, only update if valid
          const filtered: Partial<Coordinates> = {};
          if (updates.lat !== undefined && updates.lat !== null) {
            const lat = Number(updates.lat);
            if (!isNaN(lat) && isFinite(lat) && lat >= -90 && lat <= 90) {
              filtered.lat = lat;
            }
          }
          if (updates.lng !== undefined && updates.lng !== null) {
            const lng = Number(updates.lng);
            if (!isNaN(lng) && isFinite(lng) && lng >= -180 && lng <= 180) {
              filtered.lng = lng;
            }
          }

          // Only update if we have valid values
          if (Object.keys(filtered).length > 0) {
            state.coordinates = { ...state.coordinates, ...filtered };
          }
        }),
    })),
    { name: "CoordinatesStore" }
  )
);

// Selectors
export const useCoordinates = () =>
  useCoordinatesStore((state) => state.coordinates);
export const useUpdateCoordinates = () =>
  useCoordinatesStore((state) => state.updateCoordinates);

// Combined actions hook (for backward compatibility)
export const useCoordinatesActions = () => {
  const updateCoordinates = useUpdateCoordinates();
  return { updateCoordinates };
};

export default useCoordinatesStore;
